// ============================================================================
// TEMPLATES DOMAIN API CLIENT
// ============================================================================
// Domain-specific API for compliance templates and template fields
// ============================================================================

import { ApiClient } from '@core/apiClient';
import type {
  ComplianceTemplateDto,
  TemplateFieldDto,
  FieldTypeDto
} from './api/complianceStructureBuilder.dto';

// Type aliases for domain API
type Template = ComplianceTemplateDto;
type TemplateFormData = Partial<ComplianceTemplateDto>;
type FieldFormData = Partial<TemplateFieldDto>;
type FieldType = FieldTypeDto;

// ============================================================================
// NODE TYPES
// ============================================================================

export interface CreateNodeRequest {
  framework_id: number;
  parent_id?: number;
  node_type: string;
  code: string;
  title: string;
  description?: string;
  order_index: number;
}

export interface UpdateNodeRequest {
  framework_id: number;
  parent_id?: number;
  node_type: string;
  code: string;
  title: string;
  description?: string;
  order_index?: number;
}

export interface CreateFieldRequest {
  template_id: number;
  node_id?: number;
  field_type: string;
  label: string;
  field_name: string;
  placeholder?: string;
  unit?: string;
  required: boolean;
  validation_rules?: any;
  field_options?: any;
  is_active: boolean;
}

export interface UpdateFieldRequest {
  template_id: number;
  node_id?: number;
  field_type: string;
  label: string;
  field_name: string;
  placeholder?: string;
  unit?: string;
  required?: boolean;
  validation_rules?: any;
  field_options?: any;
  is_active?: boolean;
}

// ============================================================================
// TEMPLATES API CLIENT
// ============================================================================

class TemplatesApi extends ApiClient {
  constructor() {
    super({
      enableLogging: import.meta.env.DEV,
      baseURL: '/api/compliance/definition',
    });
  }

  async getTemplates(params?: {
    industry?: string;
    status?: string;
  }): Promise<Template[]> {
    const response = await this.client.get('/templates', { params });
    return response.data.data || response.data || [];
  }

  async getTemplate(id: number): Promise<Template> {
    const response = await this.client.get(`/templates/${id}`);
    return response.data;
  }

  async createTemplate(data: TemplateFormData): Promise<{ id: number }> {
    const response = await this.client.post('/templates', data);
    return response.data;
  }

  async updateTemplate(id: number, data: Partial<TemplateFormData> & { status?: string }): Promise<void> {
    await this.client.put(`/templates/${id}`, data);
  }

  async deleteTemplate(id: number, cascade: boolean = false): Promise<void> {
    await this.client.delete(`/templates/${id}${cascade ? '?cascade=true' : ''}`);
  }

  async previewDeleteTemplate(id: number): Promise<{
    template: { id: number; name: string; status: string };
    boundFrameworkVersion: { id: number; version: string; framework_id: number; framework_name: string } | null;
    additionalAssignedCompanies: number;
    mappings: number;
    nodes: number;
    fields: number;
  }> {
    const response = await this.client.get(`/templates/${id}/delete-preview`);
    return (response.data as any).preview;
  }

  async createField(templateId: number, data: FieldFormData): Promise<{ id: number }> {
    const response = await this.client.post(`/templates/${templateId}/fields`, data);
    return response.data;
  }

  async updateField(templateId: number, fieldId: number, data: Partial<FieldFormData>): Promise<void> {
    await this.client.put(`/templates/${templateId}/fields/${fieldId}`, data);
  }

  async deleteField(templateId: number, fieldId: number): Promise<void> {
    await this.client.delete(`/templates/${templateId}/fields/${fieldId}`);
  }

  // ============================================
  // NODE API METHODS
  // ============================================
  
  async createNode(templateId: number, data: CreateNodeRequest): Promise<{ id: number }> {
    const response = await this.client.post(`/templates/${templateId}/nodes`, data);
    return response.data;
  }
  
  async updateNode(templateId: number, nodeId: number, data: UpdateNodeRequest): Promise<void> {
    await this.client.put(`/templates/${templateId}/nodes/${nodeId}`, data);
  }
  
  async deleteNode(templateId: number, nodeId: number): Promise<void> {
    await this.client.delete(`/templates/${templateId}/nodes/${nodeId}`);
  }

  // ============================================
  // FIELD TYPES REGISTRY
  // ============================================

  async getFieldTypes(): Promise<FieldType[]> {
    const response = await this.client.get('/field-types');
    return response.data.data || response.data || [];
  }

  async getFieldTypeByKey(key: string): Promise<FieldType> {
    const response = await this.client.get(`/field-types/${key}`);
    return response.data.data || response.data;
  }

  async createFieldType(data: Partial<FieldType>): Promise<{ id: number }> {
    const response = await this.client.post('/field-types', data);
    return response.data.data || response.data;
  }

  async updateFieldType(id: number, data: Partial<FieldType>): Promise<void> {
    await this.client.put(`/field-types/${id}`, data);
  }

  async deleteFieldType(id: number): Promise<void> {
    await this.client.delete(`/field-types/${id}`);
  }

  async uploadFile(file: File, recordId?: number, fieldId?: number): Promise<{
    id: number;
    filename: string;
    path: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (recordId) formData.append('record_id', recordId.toString());
    if (fieldId) formData.append('field_id', fieldId.toString());

    const response = await this.client.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const templatesApi = new TemplatesApi();
